import { type JSX, useState } from 'react';
import { generatePath } from 'react-router';

import { UsersApi } from 'Api/Users/UsersApi';
import { Button } from 'Components/Buttons/Buttons';
import { OverflowMenu } from 'Components/Buttons/OverflowMenu';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { Role } from 'Components/Context/RBACContext';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE, ICON_EDIT_MODIFY_UPDATE, ICON_POSITIVE, ICON_PROHIBITED, ICON_VIEW } from 'Config/Icons';
import { SETTINGS } from 'Config/PathTemplates';
import { UCRM_ROLES, getUserNameFromUser } from 'Helpers/UserUtils';
import { AdvancedFeature } from 'Models/ClientDetailsModel';
import { Status, UserResponse } from 'Models/User';

import { DeactivateUserModal, DeactivateUserModalProps } from './DeactivateUserModal/DeactivateUserModal';
import { ManageUserModal, ManageUserModalProps } from './ManageUserModal/ManageUserModal';
import { ReactivateUserModal, ReactivateUserModalProps } from './ReactivateUserModal/ReactivateUserModal';
import { RoleDescriptionsModal } from './RoleDescriptionsModal/RoleDescriptionsModal';

export interface ManageUsersProps {
    usersApi: UsersApi;
}

enum Modal {
    None,
    Create,
    Deactivate,
    Reactivate,
    Modify,
    RoleDescriptions,
}

export const ManageUsers = (props: ManageUsersProps) => {
    const cachedData = useCachedData();
    const [displayedModal, setDisplayedModal] = useState<Modal>(Modal.None);
    const [selectedUserForDeactivation, setSelectedUserForDeactivation] = useState<UserResponse>();
    const [selectedUserForReactivation, setSelectedUserForReactivation] = useState<UserResponse>();
    const [selectedUserForModification, setSelectedUserForModification] = useState<UserResponse>();

    const userSelectedForDeactivation = (selectedUserForDeactivation: UserResponse): void => {
        setSelectedUserForDeactivation(selectedUserForDeactivation);
        setDisplayedModal(Modal.Deactivate);
    };

    const userSelectedForReactivation = (selectedUserForReactivation: UserResponse): void => {
        setSelectedUserForReactivation(selectedUserForReactivation);
        setDisplayedModal(Modal.Reactivate);
    };

    const userSelectedForModification = (selectedUserForModification: UserResponse): void => {
        setSelectedUserForModification(selectedUserForModification);
        setDisplayedModal(Modal.Modify);
    };

    const getDeactivateUserModalProps = (userSelectedForDeactivation: UserResponse): DeactivateUserModalProps => {
        return {
            hideModal: () => {
                setDisplayedModal(Modal.None);
                setSelectedUserForDeactivation(undefined);
            },
            userDeactivated: () => cachedData.refreshUsers(),
            usersApi: props.usersApi,
            userSelectedForDeactivation: userSelectedForDeactivation,
        };
    };

    const getReactivateUserModalProps = (userSelectedForReactivation: UserResponse): ReactivateUserModalProps => {
        return {
            hideModal: () => {
                setDisplayedModal(Modal.None);
                setSelectedUserForReactivation(undefined);
            },
            userReactivated: () => cachedData.refreshUsers(),
            usersApi: props.usersApi,
            userSelectedForReactivation: userSelectedForReactivation,
        };
    };

    if (cachedData.users) {
        // UCRM roles are hidden from Clients that do not have the UCRM advanced feature, so that those Clients are unaware of the module.
        const clientHasUcrm = cachedData.clientHasAdvancedFeature([AdvancedFeature.UCRM]);
        const availableRoles = Object.values(Role).filter((role) => clientHasUcrm || !UCRM_ROLES.includes(role));

        const manageUserModalProps: ManageUserModalProps = {
            allExistingUsers: cachedData.users,
            availableRoles: availableRoles,
            hideModal: () => {
                setDisplayedModal(Modal.None);
                setSelectedUserForModification(undefined);
            },
            selectedUserForModification: selectedUserForModification,
            usersApi: props.usersApi,
            userCreated: () => cachedData.refreshUsers(),
            userModified: () => cachedData.refreshUsers(),
        };

        const columns: ColumnConfig<UserResponse>[] = [
            {
                type: ColumnType.USER,
                name: 'Name',
                getUserId: (user) => user.cognito_subject,
                users: cachedData.users,
            },
            {
                type: ColumnType.STRING,
                name: 'Email',
                getString: (user) => user.email_address,
            },
            {
                type: ColumnType.STRING,
                name: 'Department',
                getString: (user) => user.department,
            },
            {
                // Custom only because this column shouldn't be sortable. Maybe there should be a STRING_ARRAY column type, or maybe the STRING column type should allow disabling sorting.
                type: ColumnType.CUSTOM,
                name: 'Roles',
                getTableCellContent: (user) => <Text noStyles>{user.roles.sort().join(', ').toUpperCase()}</Text>,
                getValue: (user) => user,
            },
            {
                type: ColumnType.STRING,
                name: 'Status',
                getString: (user) => user.status.toUpperCase(),
            },
            {
                type: ColumnType.STRING,
                name: 'MFA',
                getString: (user) => (user.mfa_required ? 'ENABLED' : 'DISABLED'),
            },
        ];

        const uniqueDepartments = [...new Set(cachedData.users.map((user) => user.department))];

        const filters: FilterConfig<UserResponse>[] = [
            {
                type: FilterType.SINGLE_PROPERTY,
                id: 'departmentFilter',
                label: 'Filter by Department',
                options: uniqueDepartments.map((deptartment) => ({ name: deptartment, value: deptartment })),
                getFilterableDataForRow: (user) => [user.department],
                canSelectMultiple: true,
            },
            {
                type: FilterType.SINGLE_PROPERTY,
                id: 'rolesFilter',
                label: 'Filter by Roles',
                options: availableRoles.map((role) => ({ name: role.toUpperCase(), value: role })),
                getFilterableDataForRow: (user) => user.roles,
                canSelectMultiple: true,
            },
            {
                type: FilterType.SINGLE_PROPERTY,
                id: 'statusFilter',
                label: 'Filter by Status',
                options: Object.entries(Status).map(([key, value]) => ({ name: key, value: value })),
                getFilterableDataForRow: (user) => [user.status],
                canSelectMultiple: true,
            },
            {
                type: FilterType.SINGLE_PROPERTY,
                id: 'mfaFilter',
                label: 'Filter by MFA',
                options: [
                    { name: 'MFA Enabled', value: 'true' },
                    { name: 'MFA Disabled', value: 'false' },
                ],
                getFilterableDataForRow: (user) => [user.mfa_required.toString()],
                canSelectMultiple: true,
            },
        ];

        const getOverflowMenuForRow = (user: UserResponse): JSX.Element => {
            const accessibilityTitle = `${getUserNameFromUser(user)} Menu`;
            if (user.status === Status.ACTIVE) {
                return (
                    <OverflowMenu
                        accessibilityTitle={accessibilityTitle}
                        overflowItems={[
                            { text: 'Modify User', onClickAction: () => userSelectedForModification(user), icon: ICON_EDIT_MODIFY_UPDATE },
                            { text: 'Deactivate User', onClickAction: () => userSelectedForDeactivation(user), icon: ICON_PROHIBITED },
                        ]}
                    />
                );
            } else {
                return <OverflowMenu accessibilityTitle={accessibilityTitle} overflowItems={[{ text: 'Reactivate User', onClickAction: () => userSelectedForReactivation(user), icon: ICON_POSITIVE }]} />;
            }
        };

        return (
            <RBACComponent roles={[Role.ADMIN]}>
                {(displayedModal === Modal.Create || displayedModal === Modal.Modify) && <ManageUserModal {...manageUserModalProps} />}
                {displayedModal === Modal.Deactivate && <DeactivateUserModal {...getDeactivateUserModalProps(selectedUserForDeactivation!)} />}
                {displayedModal === Modal.RoleDescriptions && <RoleDescriptionsModal hideModal={() => setDisplayedModal(Modal.None)} />}
                {displayedModal === Modal.Reactivate && <ReactivateUserModal {...getReactivateUserModalProps(selectedUserForReactivation!)} />}
                <PageLayoutDefault
                    headerBreadcrumb={
                        <Breadcrumb textColor="blue">
                            <BreadcrumbLink link={generatePath(SETTINGS)}>Settings</BreadcrumbLink>
                            <BreadcrumbText>User Management</BreadcrumbText>
                        </Breadcrumb>
                    }
                    headerTitle="User Management"
                    headerButtons={
                        <>
                            <Button variant="secondary" onClick={() => setDisplayedModal(Modal.RoleDescriptions)} fontAwesomeImage={ICON_VIEW}>
                                ROLE DESCRIPTIONS
                            </Button>
                            <Button variant="primary" onClick={() => setDisplayedModal(Modal.Create)} fontAwesomeImage={ICON_ADD_CREATE}>
                                CREATE USER
                            </Button>
                        </>
                    }
                    headerDescription="Create and modify user accounts. Assign roles to users to control access to SummIT features and data."
                    body={[
                        {
                            content: <Table<UserResponse> columns={columns} defaultSortColumn="Name" filters={filters} getUniqueIdForRow={(user) => user.cognito_subject} rowsData={cachedData.users} getOverflowMenuForRow={getOverflowMenuForRow} />,
                        },
                    ]}
                />
            </RBACComponent>
        );
    } else {
        return <Placeholder />;
    }
};
